import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { Button } from '@/components/primitives/Button';
import { InfoBanner } from '@/components/primitives/InfoBanner';
import { useConfirmNeedsReviewAsInternal, useNeedsReviewCount } from '@/hooks/useNeedsReviewCount';
import type { StatCardModel } from '@/types/stats';
import styles from './SubmitWeekPage.module.css';

const SUCCESS_AUTO_DISMISS_MS = 6000;

export function SubmitWeekPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // `?week` names the unit of submission (YYYY-Www ISO week), not a date
  // filter. This is the only sanctioned URL-param read on this page per
  // DESIGN-RULES.md Rule 35 — it controls which week gets submitted, not
  // which events are shown.
  const week = searchParams.get('week');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Task 204 D7-fe — wire the real needs-review count and the
  // bulk-audit helper that emits `classification.suggestion.accepted_at_submit`
  // on confirm. Mockup mode short-circuits both via the hook.
  const { count: needsReviewCount } = useNeedsReviewCount(week);
  const confirmMutation = useConfirmNeedsReviewAsInternal();

  // Three stat cards. `Need a look` is live-wired to the backend's
  // needs-review-count; the other two remain stubs pending dedicated
  // endpoints (out of scope for 204).
  const cards: readonly [StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Hours captured', value: '--' },
    { label: 'Auto-classified', value: '--' },
    {
      label: 'Need a look',
      value: needsReviewCount === null ? '--' : String(needsReviewCount),
    },
  ];

  useEffect(() => {
    if (!submitted) return;
    const id = window.setTimeout(() => setSubmitted(false), SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [submitted]);

  function handleSubmitClick() {
    // Confirmation modal fires only when there are pending-below-threshold
    // rows to confirm. If the count is zero (or still loading), submit
    // directly — the user has already classified everything.
    if (needsReviewCount && needsReviewCount > 0) {
      setConfirmOpen(true);
    } else {
      setSubmitted(true);
    }
  }

  function handleReview() {
    setConfirmOpen(false);
    void navigate('/reports/app-title-log');
  }

  async function handleSubmitAsInternal() {
    setConfirmOpen(false);
    if (week) {
      // Fire-and-forget the audit-code emission. The backend
      // emits classification.suggestion.accepted_at_submit for every
      // pending-below-threshold row in the week; the mutation hook
      // surfaces success via React Query's invalidation pipeline.
      await confirmMutation.mutateAsync({ week });
    }
    setSubmitted(true);
  }

  return (
    <>
      <PageHeader
        title="Submit Week"
        subtitle={week ?? undefined}
        actions={
          <Button variant="primary" onClick={handleSubmitClick} disabled={!week || submitted}>
            Submit week
          </Button>
        }
      />

      {submitted && (
        <InfoBanner tone="success" dismissible onDismiss={() => setSubmitted(false)}>
          Submitted. Your week is the record now.
        </InfoBanner>
      )}

      <StatCardGrid cards={cards} />

      {confirmOpen && (
        <>
          <div
            className={styles.backdrop}
            role="presentation"
            onClick={() => setConfirmOpen(false)}
          />
          <div
            className={styles.confirmDialog}
            role="alertdialog"
            aria-labelledby="submit-confirm-title"
            aria-describedby="submit-confirm-body"
          >
            <h3 id="submit-confirm-title" className={styles.confirmTitle}>
              Unclassified events
            </h3>
            <p id="submit-confirm-body" className={styles.confirmBody}>
              {needsReviewCount ?? 0} {needsReviewCount === 1 ? 'event is' : 'events are'} still
              unclassified. Submit them as Internal — Not Set, or take another minute to review?
            </p>
            <div className={styles.confirmActions}>
              <Button variant="ghost" onClick={handleReview}>
                Review
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSubmitAsInternal()}
                disabled={confirmMutation.isPending}
              >
                Submit as Internal
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
